import * as React from 'react';
import { styled, Theme, CSSObject } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Divider,
  useMediaQuery,
  Avatar,
  Typography,
  Tooltip
} from '@mui/material';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { itensMenu } from '../Menu/ItensMenu';
import Logout from './Logout';
import { useSessao } from '../../redux/sessao/sessaoHooks';
import { COR_PRINCIPAL_DEFAULT } from '../../constants';

const drawerOpenWidth = 280;
const drawerClosedWidth = 65;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerOpenWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
});

const closedMixin = (theme: Theme): CSSObject => ({
  width: drawerClosedWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden'
});

const DrawerComponent = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open'
})(({ theme, open }) => ({
  width: drawerOpenWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    borderRight: 'none',
    background: `linear-gradient(180deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)`,
    boxShadow: '2px 0 20px rgba(0,0,0,0.08)',
    ...(open ? openedMixin(theme) : closedMixin(theme))
  }
}));

type Props = {
  open: boolean;
  setOpen: (x: boolean) => void;
};

const Drawer: React.FC<Props> = ({ open, setOpen }) => {
  const isMobile = useMediaQuery('(max-width:960px)');
  const location = useLocation();
  const navigate = useNavigate();
  const [sessao] = useSessao();
  const corPrincipal = sessao?.empresa?.corPrincipal || COR_PRINCIPAL_DEFAULT;
  const clienteLogado = sessao?.cliente;
  const primeiroNome = clienteLogado?.nome.split(' ')[0] ?? '';
  const nomeCliente = `${primeiroNome.charAt(0).toUpperCase()}${primeiroNome
    .slice(1)
    .toLowerCase()}`;

  const email = sessao?.cliente?.email?.trim();
  const celular = sessao?.cliente?.celular?.trim()
    ? `${sessao?.cliente?.dddCelular?.trim()} ${sessao?.cliente?.celular?.trim()}`
    : '';

  const getUserInitials = (name: string) => {
    return (
      name
        ?.split(' ')
        .map((word) => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'U'
    );
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === `/${path}` || location.pathname === path;
  };

  const renderUserInfo = () => (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        transition: 'all 0.3s ease',
        borderBottom: `1px solid`,
        borderColor: 'divider',
        background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(248,250,252,0.9) 100%)'
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        sx={{ transition: 'opacity 0.2s ease', cursor: 'pointer' }}
        onClick={() => {
          navigate('/perfil');
        }}
      >
        <Avatar
          sx={{
            width: 40,
            height: 40,
            fontSize: '1rem',
            fontWeight: 600,
            backgroundColor: corPrincipal,
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.05)'
            }
          }}
        >
          {getUserInitials(sessao?.cliente?.nome || '')}
        </Avatar>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight="600" noWrap sx={{ color: 'text.primary' }}>
            {nomeCliente}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.75rem' }}>
            {email || celular}
          </Typography>
        </Box>
      </Box>
    </Box>
  );

  const renderMenuItems = () => (
    <List
      sx={{
        px: 1.5,
        py: 2,
        flex: 1
      }}
    >
      {itensMenu
        .filter((item) => item.show)
        .map((item, index) => {
          const isActive = isActiveRoute(item.path);

          return (
            <React.Fragment key={index}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <Tooltip title={!open ? item.label : ''} placement="right" arrow>
                  <ListItemButton
                    component={Link}
                    to={item.path}
                    sx={{
                      padding: 1,
                      borderRadius: 2,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        backgroundColor: isActive ? `${corPrincipal}20` : 'rgba(0, 0, 0, 0.04)',
                        border: isActive
                          ? `1px solid ${corPrincipal}40`
                          : '1px solid rgba(0, 0, 0, 0.1)',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                      },
                      '&.Mui-focusVisible': {
                        backgroundColor: `${corPrincipal}20`
                      }
                    }}
                    onClick={() => isMobile && setOpen(false)}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 40,
                        color: isActive ? corPrincipal : 'text.secondary',
                        transition: 'all 0.3s ease',
                        fontSize: '1.25rem'
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>

                    <ListItemText
                      primary={item.label}
                      sx={{
                        opacity: open ? 1 : 0,
                        transition: 'opacity 0.2s ease 0.1s',
                        '& .MuiTypography-root': {
                          fontWeight: isActive ? 600 : 400,
                          color: isActive ? corPrincipal : 'text.primary',
                          fontSize: '0.9rem'
                        }
                      }}
                    />
                  </ListItemButton>
                </Tooltip>
              </ListItem>

              {index < itensMenu.filter((item) => item.show).length - 1 && (
                <Divider
                  sx={{
                    my: 0,
                    opacity: 0.3
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
    </List>
  );

  const renderList = () => (
    <Box
      display="flex"
      flexDirection="column"
      height="100%"
      sx={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
      }}
    >
      {renderUserInfo()}
      {renderMenuItems()}
      <Box sx={{ p: 2, mt: 'auto' }}>
        <Logout open={open} />
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <MuiDrawer
          open={open}
          onClose={() => setOpen(false)}
          variant="temporary"
          ModalProps={{
            keepMounted: true
          }}
          sx={{
            '& .MuiDrawer-paper': {
              background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)',
              boxShadow: '2px 0 20px rgba(0,0,0,0.15)'
            }
          }}
        >
          {renderList()}
        </MuiDrawer>
      ) : (
        <DrawerComponent
          variant="permanent"
          open={open}
          onMouseEnter={() => !isMobile && setOpen(true)}
          onMouseLeave={() => !isMobile && setOpen(false)}
        >
          {renderList()}
        </DrawerComponent>
      )}
    </>
  );
};

export default Drawer;
